import React, { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { Box, Layout, Cell, Heading } from '@wix/design-system';
import { useTranslation, useExperiments } from '@wix/fe-essentials-standalone';
import {
  ACTIONS,
  useTemplateContext,
} from '@components/Contexts/TemplateContext';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import LeftSectionLayout from './LeftSectionLayout';
import CreatorBox from './CreatorBox';
import Features from './Features';
import { Sections, ENABLE_MORE_TEMPLATES_YOU_MAY_LIKE } from '@utils/constants';
import { MAX_CHARACTER_LIMIT } from './constant';
import Hero from './Hero';
import ContentStickyHeader from './ContentStickyHeader';
import TemplateReviews from './TemplateReviews';
import MobileTemplatePage from './MobileTemplatePage';
import TemplatePageHeader from './TemplatePageHeader';
import PurchaseErrorNotification from './PurchaseErrorNotification';
import IndustryAndLanguage from './IndustryAndLanguage';
import FullWidthCarousel from './FullWidthCarousel';
import { useTemplateEntry } from '@components/Hooks/useTemplateEntry';
import { YouMayAlsoLike, MoreByPartner } from './RecommendedTemplates';
import { usePreviouslyViewedTemplates } from './RecommendedTemplates/usePreviouslyViewedTemplates';
import ReportTemplateErrorNotification from './AdditionalInfo/ReportTemplateModal/ReportTemplateErrorNotification';
import { useReportTemplateForm } from '@components/Hooks/useReportTemplate';
import ShowMoreText from '@components/ShowMoreText';
import s from './Template.module.scss';

function Template() {
  const { isMobileUserAgent, template } = useTemplateContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const { experiments } = useExperiments({ readOnly: true });
  const { logBiPageView } = useTemplateBi({ is_mobile: isMobileView });
  const { entry } = useTemplateEntry();
  const [purchaseError, setPurchaseError] = useState<boolean>(false);

  const enableMoreTemplateYouMayLike = experiments.enabled(
    ENABLE_MORE_TEMPLATES_YOU_MAY_LIKE,
  );

  usePreviouslyViewedTemplates(template.id, template.accountId);

  useEffect(() => {
    if (entry !== undefined) {
      logBiPageView();
    }
  }, [entry]);

  if (isMobileView) {
    return (
      <MobileTemplatePage
        enableMoreTemplateYouMayLike={enableMoreTemplateYouMayLike}
        purchaseError={purchaseError}
        setPurchaseError={setPurchaseError}
      />
    );
  }

  return (
    <TemplatePage
      enableMoreTemplateYouMayLike={enableMoreTemplateYouMayLike}
      purchaseError={purchaseError}
      setPurchaseError={setPurchaseError}
    />
  );
}

interface TemplatePageProps {
  purchaseError: boolean;
  setPurchaseError: (isError: boolean) => void;
  enableMoreTemplateYouMayLike: boolean;
}

const TemplatePage: FC<TemplatePageProps> = ({
  purchaseError,
  setPurchaseError,
  enableMoreTemplateYouMayLike,
}) => {
  const { t } = useTranslation();
  const {
    template,
    showStickyCTAs,
    navigationRefs,
    reportTemplateData: { showReportTemplateError },
    dispatch,
  } = useTemplateContext();
  const { logBiClick } = useTemplateBi({ is_mobile: false });
  const { onSubmit } = useReportTemplateForm({
    listingId: template.id,
  });
  return (
    <Box direction="vertical" marginBottom="152px">
      <TemplatePageHeader isMobile={false} />
      <ReportTemplateErrorNotification
        onSubmit={onSubmit}
        setShowReportTemplateError={(show: boolean) =>
          dispatch({
            type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
            payload: { showReportTemplateError: show },
          })
        }
        showReportTemplateError={showReportTemplateError}
      />
      <PurchaseErrorNotification
        isMobileView={false}
        isError={purchaseError}
        setPurchaseError={setPurchaseError}
      />

      <Hero setPurchaseError={setPurchaseError} />
      <FullWidthCarousel />
      <ContentStickyHeader setPurchaseError={setPurchaseError} />
      <Box
        display="block"
        dataHook="template-page"
        backgroundColor="#fff"
        width="100%"
        maxWidth="1280px"
        minWidth="926px"
        margin="auto"
        padding="0 48px"
        boxSizing="border-box"
      >
        <Box direction="vertical" align="center">
          <Box marginTop="70px" align="left" width="100%">
            <Layout cols={13}>
              <LeftSectionLayout
                ref={navigationRefs}
                sectionId={Sections.ABOUT}
              >
                <Box direction="vertical" gap="6px" marginBottom="46px">
                  <Box paddingBottom="18px" dataHook="template-about-title">
                    <Heading size="large" as="h2">
                      {t('marketplace.templates-page.about-title')}
                    </Heading>
                  </Box>
                  <ShowMoreText
                    textSize="medium"
                    maxChars={MAX_CHARACTER_LIMIT}
                    originalText={template.description}
                    handleShowMoreClick={() =>
                      logBiClick({ clickType: 'Show more about' })
                    }
                    showMoreInitialState={true}
                    buttonWeight="thin"
                    className={s.showMoreTextContainer}
                  />
                  <IndustryAndLanguage
                    direction="horizontal"
                    textSize="medium"
                  />
                </Box>
              </LeftSectionLayout>
              <Cell span={1}></Cell>
              <Cell span={4} rows={3}>
                <CreatorBox
                  isMobileView={false}
                  showAdditionalInfo={true}
                  isSticky={showStickyCTAs}
                />
              </Cell>
              {!_.isEmpty(template?.apps) && (
                <LeftSectionLayout
                  ref={navigationRefs}
                  sectionId={Sections.FEATURED}
                >
                  <Box>
                    <Features isMobileView={false} />
                  </Box>
                </LeftSectionLayout>
              )}
              <LeftSectionLayout
                ref={navigationRefs}
                sectionId={Sections.REVIEWS}
              >
                <Box marginTop="46px">
                  <TemplateReviews
                    isMobileView={false}
                    showReviewsCount={true}
                  />
                </Box>
              </LeftSectionLayout>
            </Layout>
          </Box>
          {enableMoreTemplateYouMayLike && (
            <Box paddingTop="65px" direction="vertical" width="100%">
              <YouMayAlsoLike />
              <MoreByPartner />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Template;
